import React, { useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Building2, Users, CreditCard, Shield, AlertCircle, CheckCircle, 
  Clock, ArrowUpRight, ArrowDownLeft, RefreshCw, Settings, 
  Eye, Banknote, UserCheck, FileText, Activity
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = getApiBaseUrl();

const GriffinAdminDashboard = () => {
  const [status, setStatus] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [configUpdates, setConfigUpdates] = useState({});

  const fetchStatus = useCallback(async () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/griffin/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Error fetching Griffin status:', error);
    }
  }, []);

  const fetchDashboard = useCallback(async () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/griffin/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Error fetching Griffin dashboard:', error);
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/griffin/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConfig(data.config);
      }
    } catch (error) {
      console.error('Error fetching Griffin config:', error);
    }
  }, []);

  const updateConfig = async () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/griffin/config`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(configUpdates)
      });
      if (response.ok) {
        toast.success('Configuration updated successfully');
        fetchConfig();
        setConfigUpdates({});
      } else {
        toast.error('Failed to update configuration');
      }
    } catch (error) {
      console.error('Error updating config:', error);
      toast.error('Error updating configuration');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStatus(), fetchDashboard(), fetchConfig()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStatus, fetchDashboard, fetchConfig]);

  const handleConfigChange = (key, value) => {
    setConfigUpdates(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="griffin-admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-blue-600" />
            Griffin Banking Dashboard
          </h1>
          <p className="text-gray-500 mt-1">Banking-as-a-Service Integration</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge 
            variant={status?.is_placeholder ? "warning" : "success"}
            className={`${status?.is_placeholder ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}
          >
            {status?.is_placeholder ? 'Placeholder Mode' : status?.mode || 'Unknown'}
          </Badge>
          <Button variant="outline" size="sm" onClick={() => {
            fetchStatus();
            fetchDashboard();
          }}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Status Alert */}
      {status?.is_placeholder && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Placeholder API Keys Detected</p>
              <p className="text-sm text-amber-700 mt-1">{status?.message}</p>
              <p className="text-sm text-amber-600 mt-2">
                Configure Griffin API credentials below to enable full banking functionality.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="accounts" className="flex items-center gap-2">
            <Banknote className="w-4 h-4" />
            Accounts
          </TabsTrigger>
          <TabsTrigger value="verification" className="flex items-center gap-2">
            <UserCheck className="w-4 h-4" />
            KYC/KYB
          </TabsTrigger>
          <TabsTrigger value="config" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Configuration
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Legal Persons Card */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Legal Persons
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboard?.total_legal_persons || 0}</div>
                <div className="flex items-center gap-2 mt-2 text-sm">
                  <Badge variant="outline" className="bg-green-50 text-green-700">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {dashboard?.verified_legal_persons || 0} Verified
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Bank Accounts Card */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Bank Accounts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboard?.total_accounts || 0}</div>
                <div className="flex items-center gap-2 mt-2 text-sm">
                  <Badge variant="outline" className="bg-blue-50 text-blue-700">
                    {dashboard?.open_accounts || 0} Open
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Total Balance Card */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <Banknote className="w-4 h-4" />
                  Total Balance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  £{parseFloat(dashboard?.total_balance || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
                <p className="text-xs text-gray-500 mt-2">Across all accounts</p>
              </CardContent>
            </Card>

            {/* Payments Today Card */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Payments Today
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboard?.total_payments_today || 0}</div>
                <div className="text-sm text-gray-500 mt-2">
                  £{parseFloat(dashboard?.payments_value_today || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Pending Verifications */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-500" />
                  Pending Verifications
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-amber-600">
                  {dashboard?.pending_verifications || 0}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  KYC/KYB checks awaiting completion
                </p>
              </CardContent>
            </Card>

            {/* Pending Payments */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ArrowUpRight className="w-5 h-5 text-blue-500" />
                  Pending Payments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">
                  {dashboard?.pending_payments || 0}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Payments being processed
                </p>
              </CardContent>
            </Card>

            {/* Integration Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-500" />
                  Integration Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Organization</span>
                    {status?.organization_configured ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Operational Account</span>
                    {status?.accounts_configured?.operational ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Webhooks</span>
                    {status?.webhook_configured ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Accounts Tab */}
        <TabsContent value="accounts">
          <Card>
            <CardHeader>
              <CardTitle>Bank Accounts</CardTitle>
              <CardDescription>Client money accounts and operational accounts</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No bank accounts configured yet.</p>
                <p className="text-sm mt-2">
                  Accounts will appear here once landlords complete Griffin onboarding.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Verification Tab */}
        <TabsContent value="verification">
          <Card>
            <CardHeader>
              <CardTitle>KYC/KYB Verifications</CardTitle>
              <CardDescription>Identity verification status for tenants, landlords, and contractors</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No verifications pending.</p>
                <p className="text-sm mt-2">
                  Verification requests will appear here when users begin onboarding.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="config">
          <Card>
            <CardHeader>
              <CardTitle>Griffin Configuration</CardTitle>
              <CardDescription>
                Configure Griffin API settings. Contact Griffin to obtain sandbox or production credentials.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Organization Settings */}
              <div className="space-y-4">
                <h3 className="font-medium text-lg">Organization</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="organization_id">Organization ID</Label>
                    <Input
                      id="organization_id"
                      placeholder="org-xxxxxxxxxx"
                      defaultValue={config?.organization_id?.value || ''}
                      onChange={(e) => handleConfigChange('organization_id', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="organization_url">Organization URL</Label>
                    <Input
                      id="organization_url"
                      placeholder="/v0/organizations/org-xxx"
                      defaultValue={config?.organization_url?.value || ''}
                      onChange={(e) => handleConfigChange('organization_url', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Account Settings */}
              <div className="space-y-4">
                <h3 className="font-medium text-lg">Accounts</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="operational_account_id">Operational Account ID</Label>
                    <Input
                      id="operational_account_id"
                      placeholder="acc-xxxxxxxxxx"
                      defaultValue={config?.operational_account_id?.value || ''}
                      onChange={(e) => handleConfigChange('operational_account_id', e.target.value)}
                    />
                    <p className="text-xs text-gray-500">For agency fees and commissions</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="deposit_holding_account_id">Deposit Holding Account ID</Label>
                    <Input
                      id="deposit_holding_account_id"
                      placeholder="acc-xxxxxxxxxx"
                      defaultValue={config?.deposit_holding_account_id?.value || ''}
                      onChange={(e) => handleConfigChange('deposit_holding_account_id', e.target.value)}
                    />
                    <p className="text-xs text-gray-500">For tenant deposit protection</p>
                  </div>
                </div>
              </div>

              {/* Workflow Settings */}
              <div className="space-y-4">
                <h3 className="font-medium text-lg">Verification Workflows</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="tenant_workflow_url">Tenant Workflow URL</Label>
                    <Input
                      id="tenant_workflow_url"
                      placeholder="/v0/workflows/wfl-xxx"
                      defaultValue={config?.tenant_workflow_url?.value || ''}
                      onChange={(e) => handleConfigChange('tenant_workflow_url', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="landlord_workflow_url">Landlord Workflow URL</Label>
                    <Input
                      id="landlord_workflow_url"
                      placeholder="/v0/workflows/wfl-xxx"
                      defaultValue={config?.landlord_workflow_url?.value || ''}
                      onChange={(e) => handleConfigChange('landlord_workflow_url', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_workflow_url">Company (KYB) Workflow URL</Label>
                    <Input
                      id="company_workflow_url"
                      placeholder="/v0/workflows/wfl-xxx"
                      defaultValue={config?.company_workflow_url?.value || ''}
                      onChange={(e) => handleConfigChange('company_workflow_url', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="webhook_secret">Webhook Secret</Label>
                    <Input
                      id="webhook_secret"
                      type="password"
                      placeholder="whsec_xxxxxxxxxx"
                      defaultValue={config?.webhook_secret?.value || ''}
                      onChange={(e) => handleConfigChange('webhook_secret', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Save Button */}
              {Object.keys(configUpdates).length > 0 && (
                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={updateConfig}>
                    Save Configuration
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GriffinAdminDashboard;
